import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [settings, setSettings] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const sectionRef = useRef(null);

  useEffect(() => {
    fetchSettings();
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      toast({
        title: "Message Sent!",
        description: "We'll get back to you soon.",
      });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section ref={sectionRef} className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-light text-[#2d2d2d] mb-8 md:mb-12 text-center">
          Get in Touch.
        </h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16">
          {/* Contact Info */}
          <div
            className="space-y-8"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
            }}
          >
            <div>
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-light text-[#2d2d2d] mb-6">Contact Information</h3>
              <p className="text-[#2d2d2d] font-light text-lg leading-relaxed">
                Ready to take the next step? Reach out to us through any of the following channels,
                and our team will respond promptly.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4 group cursor-pointer">
                <div className="w-12 h-12 bg-[#0A1A50]/20 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-[#0A1A50]/30 transition-colors">
                  <Mail className="w-6 h-6 text-[#0A1A50]" />
                </div>
                <div>
                  <h4 className="text-lg font-normal text-[#2d2d2d] mb-1">Email</h4>
                  <a
                    href={`mailto:${settings.contact_email || 'info@reagle.ae'}`}
                    className="text-[#2d2d2d] hover:text-[#0A1A50] transition-colors font-light"
                  >
                    {settings.contact_email || 'info@reagle.ae'}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4 group cursor-pointer">
                <div className="w-12 h-12 bg-[#0A1A50]/20 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-[#0A1A50]/30 transition-colors">
                  <Phone className="w-6 h-6 text-[#0A1A50]" />
                </div>
                <div>
                  <h4 className="text-lg font-normal text-[#2d2d2d] mb-1">Phone</h4>
                  <a
                    href={`tel:${settings.contact_phone || '+971 4 XXX XXXX'}`}
                    className="text-[#2d2d2d] hover:text-[#0A1A50] transition-colors font-light"
                  >
                    {settings.contact_phone || '+971 4 XXX XXXX'}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4 group cursor-pointer">
                <div className="w-12 h-12 bg-[#0A1A50]/20 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-[#0A1A50]/30 transition-colors">
                  <MapPin className="w-6 h-6 text-[#0A1A50]" />
                </div>
                <div>
                  <h4 className="text-lg font-normal text-[#2d2d2d] mb-1">Location</h4>
                  <p className="text-[#2d2d2d] font-light">
                    {settings.contact_address || 'Dubai, United Arab Emirates'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div
            className="bg-gray-50 rounded-2xl p-6 sm:p-8"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'opacity 0.8s ease-out 0.2s, transform 0.8s ease-out 0.2s'
            }}
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="bg-white border-gray-300 focus:border-[#0A1A50] focus:ring-[#0A1A50]"
                />
              </div>
              <div>
                <Input
                  type="email"
                  name="email"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-white border-gray-300 focus:border-[#0A1A50] focus:ring-[#0A1A50]"
                />
              </div>
              <div>
                <Input
                  type="tel"
                  name="phone"
                  placeholder="Your Phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="bg-white border-gray-300 focus:border-[#0A1A50] focus:ring-[#0A1A50]"
                />
              </div>
              <div>
                <Textarea
                  name="message"
                  placeholder="Your Message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="bg-white border-gray-300 focus:border-[#0A1A50] focus:ring-[#0A1A50] resize-none"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#0A1A50] hover:bg-[#060F2E] text-white py-6 text-lg rounded-full group"
              >
                Send Message
                <Send className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
